import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile, Molecule, Project } from '../../types/chemistry';
import MoleculeGrid from './MoleculeGrid';
import CompoundDetailPanel, { DetailTab } from './CompoundDetailPanel';
import FilePanel, { cleanFileName } from './FilePanel';
import FileList from './FileList';
import MergeDialog from './MergeDialog';
import { useChemistryStore } from '../../stores/chemistryStore';
import apiClient from '../../api/client';

interface ProjectCompoundsTabProps {
  projectId: number;
}

const CHEM_EXT = new Set(['sdf','mol','mol2','smi','smiles','csv','xlsx','xls','cdx','cdxml','cml','inchi','rxn','rdf']);

// file_type holds a content category on some rows and a bare file extension on others,
// and is NULL on everything the unified importer writes. Only trust it as a category
// when it actually is one — 'reactions' here outranks a 'compound' category, which the
// importer stamps on reaction files too.
const CONTENT_TYPES = new Set(['molecules', 'reactions', 'biologics']);

const isChemFile = (f: ChemFile) => {
  if (f.fileType && CONTENT_TYPES.has(f.fileType)) return f.fileType === 'molecules';
  // Categories survive extraction from a PPTX or PDF, whose extension says nothing
  // about the content it yielded.
  if (f.fileCategories?.includes('compound')) return true;
  // No usable category — fall back to extension check (backward compat for older files)
  // Check originalFileName first, then fileName (originalFileName may lack extension after rename)
  const origExt = (f.originalFileName || '').split('.').pop()?.toLowerCase();
  if (origExt && CHEM_EXT.has(origExt)) return true;
  const ext = (f.fileName || '').split('.').pop()?.toLowerCase();
  return !!ext && CHEM_EXT.has(ext);
};

// ─── localStorage helpers ────────────────────────────────────

const LAST_VIEWED_KEY = 'projectLastViewedAt';
const READ_KEY_PREFIX = 'discoverant_read_';
// PANEL_MODE_KEY, FILE_LIST_VIEW_KEY now in FilePanel

function getLastViewedAt(projectId: number): number {
  try {
    const stored = localStorage.getItem(LAST_VIEWED_KEY);
    if (!stored) return 0;
    const map = JSON.parse(stored);
    return map[String(projectId)] || 0;
  } catch { return 0; }
}

function setLastViewedAt(projectId: number): void {
  try {
    const stored = localStorage.getItem(LAST_VIEWED_KEY);
    const map = stored ? JSON.parse(stored) : {};
    map[String(projectId)] = Date.now();
    localStorage.setItem(LAST_VIEWED_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
}

function loadReadMolecules(projectId: number): Set<number> {
  try {
    const stored = localStorage.getItem(`${READ_KEY_PREFIX}${projectId}`);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch { return new Set(); }
}

function saveReadMolecules(projectId: number, ids: Set<number>): void {
  try {
    localStorage.setItem(`${READ_KEY_PREFIX}${projectId}`, JSON.stringify(Array.from(ids)));
  } catch { /* ignore */ }
}

function isFileNew(file: ChemFile, lastViewedAt: number): boolean {
  if (lastViewedAt === 0) return false;
  const modified = file.modifiedDate ? new Date(file.modifiedDate).getTime() : 0;
  const created = file.createdDate ? new Date(file.createdDate).getTime() : 0;
  return Math.max(modified, created) > lastViewedAt;
}

// loadPanelMode, loadFileListView now in FilePanel

// ─── Sort options ────────────────────────────────────────────

type Level2Sort = 'recent' | 'name' | 'molecules';
type WhatsNewSort = 'newest' | 'oldest' | 'byFile' | 'unreadFirst';

// Panel widths now in FilePanel

// ═════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════

const ProjectCompoundsTab: React.FC<ProjectCompoundsTabProps> = ({ projectId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedFileIds, setSelectedFileIds, clearSelection } = useChemistryStore();

  // Ref to avoid setView depending on searchParams (which changes on every URL update)
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // URL-derived state
  const viewParam = searchParams.get('view');
  const fileParam = searchParams.get('file');
  const navigationLevel: 1 | 2 = viewParam === 'files' ? 2 : 1;
  const activeFileId = fileParam || null;

  // Core data
  const [files, setFiles] = useState<ChemFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allMolecules, setAllMolecules] = useState<Molecule[] | null>(null);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [detailMolecule, setDetailMolecule] = useState<Molecule | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab | null>(null);
  const [isDetailPinned, setIsDetailPinned] = useState(false);
  const fetchCancelledRef = useRef(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{ duplicateCount: number } | null>(null);

  // Read/unread tracking
  const [readMoleculeIds, setReadMoleculeIds] = useState<Set<number>>(() => loadReadMolecules(projectId));
  const [lastViewedAt] = useState(() => getLastViewedAt(projectId));

  // View state (panel mode now in FilePanel)
  const [level2Sort, setLevel2Sort] = useState<Level2Sort>('recent');
  const [whatsNewSort, setWhatsNewSort] = useState<WhatsNewSort>('newest');
  // Selected molecules in the grid (for mark read/unread controls)
  const [selectedMoleculeIds, setSelectedMoleculeIds] = useState<number[]>([]);

  // Bulk action state for Level 2 FileList
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isMoveDropdownOpen, setIsMoveDropdownOpen] = useState(false);
  const [isMovingFiles, setIsMovingFiles] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);

  // Fetch projects for Move menu
  useEffect(() => {
    chemistryApi.getProjects().then(setProjects).catch(() => {});
  }, []);

  // Update lastViewedAt after a delay
  useEffect(() => {
    const timer = setTimeout(() => setLastViewedAt(projectId), 2000);
    return () => clearTimeout(timer);
  }, [projectId]);

  // Ctrl+B keyboard shortcut now in FilePanel

  // ─── Fetch files ──────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const allFiles = await chemistryApi.getFiles(projectId);
        if (!cancelled) setFiles(allFiles.filter(isChemFile));
      } catch { /* ignore */ }
      finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ─── Fetch duplicate info ─────────────────────────────────

  useEffect(() => {
    chemistryApi.getProjectDuplicates(projectId)
      .then(info => setDuplicateInfo({ duplicateCount: info.duplicateCount }))
      .catch(() => {});
  }, [projectId]);

  // ─── All Compounds navigation handler ───────────────────

  const handleAllCompoundsClick = useCallback(() => {
    navigate(`/chemistry/file/project-${projectId}/editor`);
  }, [navigate, projectId]);

  // ─── Compute new file info ────────────────────────────────

  const newFileIds = useMemo(() => {
    const ids = new Set<string>();
    files.forEach(f => { if (isFileNew(f, lastViewedAt)) ids.add(f.id); });
    return ids;
  }, [files, lastViewedAt]);

  // ─── Per-file unread counts ───────────────────────────────

  const fileUnreadCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (allMolecules) {
      for (const mol of allMolecules) {
        const fileId = (mol as any)._sourceFileId as string;
        if (!fileId || !newFileIds.has(fileId)) continue;
        if (!readMoleculeIds.has(mol.id)) {
          counts.set(fileId, (counts.get(fileId) || 0) + 1);
        }
      }
    } else {
      for (const f of files) {
        if (newFileIds.has(f.id)) {
          counts.set(f.id, f.moleculesLibraryCount);
        }
      }
    }
    return counts;
  }, [allMolecules, files, newFileIds, readMoleculeIds]);

  const totalUnreadCount = useMemo(() => {
    let total = 0;
    fileUnreadCounts.forEach(c => { total += c; });
    return total;
  }, [fileUnreadCounts]);

  // ─── Fetch ALL molecules (for What's New) ─────────────────

  const fetchAllMolecules = useCallback(async (chemFiles: ChemFile[]) => {
    fetchCancelledRef.current = false;
    setIsLoadingAll(true);
    try {
      const all: Molecule[] = [];
      for (const file of chemFiles) {
        if (fetchCancelledRef.current) break;
        const { molecules } = await chemistryApi.getMolecules(file.id, 1, 50000);
        molecules.forEach(m => {
          (m as any)._sourceFileName = cleanFileName(file.originalFileName || file.fileName);
          (m as any)._sourceFileId = file.id;
        });
        all.push(...molecules);
      }
      if (!fetchCancelledRef.current) setAllMolecules(all);
    } catch { /* ignore */ }
    finally { setIsLoadingAll(false); }
  }, []);

  // Load all molecules when at Level 1
  useEffect(() => {
    if (files.length > 0 && navigationLevel === 1 && allMolecules === null) {
      fetchAllMolecules(files);
    }
  }, [files, navigationLevel, allMolecules, fetchAllMolecules]);

  // ─── Sorted molecules for What's New ──────────────────────

  const sortedWhatsNewMolecules = useMemo(() => {
    if (!allMolecules) return null;
    const sorted = [...allMolecules];
    const isUnread = (m: Molecule) =>
      newFileIds.has((m as any)._sourceFileId) && !readMoleculeIds.has(m.id);

    switch (whatsNewSort) {
      case 'newest':
        sorted.sort((a, b) => {
          const aUnread = isUnread(a) ? 0 : 1;
          const bUnread = isUnread(b) ? 0 : 1;
          if (aUnread !== bUnread) return aUnread - bUnread;
          return b.mol_index - a.mol_index;
        });
        break;
      case 'oldest':
        sorted.sort((a, b) => a.mol_index - b.mol_index);
        break;
      case 'byFile':
        sorted.sort((a, b) => {
          const aFile = (a as any)._sourceFileName || '';
          const bFile = (b as any)._sourceFileName || '';
          if (aFile !== bFile) return aFile.localeCompare(bFile);
          return a.mol_index - b.mol_index;
        });
        break;
      case 'unreadFirst':
        sorted.sort((a, b) => {
          const aUnread = isUnread(a) ? 0 : 1;
          const bUnread = isUnread(b) ? 0 : 1;
          return aUnread - bUnread;
        });
        break;
    }
    return sorted;
  }, [allMolecules, whatsNewSort, newFileIds, readMoleculeIds]);

  // ─── Read/unread actions ──────────────────────────────────

  const markMoleculeRead = useCallback((moleculeId: number) => {
    setReadMoleculeIds(prev => {
      if (prev.has(moleculeId)) return prev;
      const next = new Set(prev);
      next.add(moleculeId);
      saveReadMolecules(projectId, next);
      return next;
    });
  }, [projectId]);

  const markAllRead = useCallback(() => {
    if (!allMolecules) return;
    setReadMoleculeIds(prev => {
      const next = new Set(prev);
      for (const mol of allMolecules) {
        if (newFileIds.has((mol as any)._sourceFileId)) {
          next.add(mol.id);
        }
      }
      saveReadMolecules(projectId, next);
      return next;
    });
  }, [allMolecules, newFileIds, projectId]);

  const markSelectedRead = useCallback(() => {
    setReadMoleculeIds(prev => {
      const next = new Set(prev);
      selectedMoleculeIds.forEach(id => next.add(id));
      saveReadMolecules(projectId, next);
      return next;
    });
  }, [selectedMoleculeIds, projectId]);

  const markSelectedUnread = useCallback(() => {
    setReadMoleculeIds(prev => {
      const next = new Set(prev);
      selectedMoleculeIds.forEach(id => next.delete(id));
      saveReadMolecules(projectId, next);
      return next;
    });
  }, [selectedMoleculeIds, projectId]);

  // ─── URL navigation ───────────────────────────────────────

  const setView = useCallback((view: 'new' | 'files' | null) => {
    const params = new URLSearchParams(searchParamsRef.current);
    if (view) {
      params.set('view', view);
    } else {
      params.delete('view');
    }
    params.delete('file');
    setSearchParams(params, { replace: true });
    setDetailMolecule(null);
  }, [setSearchParams]);

  const handleWhatsNewClick = useCallback(() => {
    setAllMolecules(null);
    setView('new');
  }, [setView]);

  const handleExpandToLevel2 = useCallback(() => {
    setView('files');
  }, [setView]);

  // Refetch files (after merge/move/delete)
  const refetchFiles = useCallback(async () => {
    try {
      const allFiles = await chemistryApi.getFiles(projectId);
      setFiles(allFiles.filter(isChemFile));
      clearSelection();
    } catch { /* ignore */ }
  }, [projectId, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedFileIds.length === 0) return;
    if (window.confirm(`Delete ${selectedFileIds.length} file(s)? This cannot be undone.`)) {
      try {
        await chemistryApi.deleteFiles(selectedFileIds);
        await refetchFiles();
      } catch (err: any) {
        console.error('Delete failed:', err);
        window.alert(
          err?.response?.data?.detail
            || 'Something went wrong while deleting the files. Refresh the page and try again.',
        );
        await refetchFiles();
      }
    }
  }, [selectedFileIds, refetchFiles]);

  const handleBulkMove = useCallback(async (targetProjectId: number) => {
    if (selectedFileIds.length === 0) return;
    setIsMovingFiles(true);
    try {
      await chemistryApi.bulkMoveFiles(selectedFileIds, targetProjectId);
      setIsMoveDropdownOpen(false);
      refetchFiles();
    } catch (err) { console.error('Move failed:', err); }
    finally { setIsMovingFiles(false); }
  }, [selectedFileIds, refetchFiles]);

  const handleDownload = useCallback(async (format: string) => {
    if (selectedFileIds.length === 0) return;
    setIsDownloading(true);
    setIsDownloadDropdownOpen(false);
    try {
      for (const fileId of selectedFileIds) {
        const file = files.find(f => f.id === fileId);
        if (!file) continue;
        const response = await apiClient.post('/export/molecules', {
          file_id: Number(fileId),
          molecule_ids: [],
          format,
        }, { responseType: 'blob' });

        const baseName = (file.originalFileName || file.fileName).replace(/^[a-f0-9]{8}_/i, '').replace(/\.[^.]+$/, '');
        const extMap: Record<string, string> = { sdf: '.sdf.gz', csv: '.csv', smi: '.smi' };
        const filename = baseName + (extMap[format] || `.${format}`);

        const blob = new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error('Download failed:', err); }
    finally { setIsDownloading(false); }
  }, [selectedFileIds, files]);

  const handleRowClick = useCallback((molecule: Molecule) => {
    if (detailMolecule?.id === molecule.id && !isDetailPinned) {
      setDetailMolecule(null);
    } else {
      setDetailMolecule(molecule);
      setDetailTab(null);
      markMoleculeRead(molecule.id);
    }
  }, [detailMolecule, isDetailPinned, markMoleculeRead]);

  const handleSelectionChange = useCallback((mols: Molecule[]) => {
    setSelectedMoleculeIds(mols.map(m => m.id));
  }, []);

  // On mount: default to file table view (Level 2) when no view param is set
  useEffect(() => {
    if (files.length > 0 && !isLoading && !viewParam) {
      setView('files');
    }
  }, [files, isLoading, viewParam, setView]);

  // ─── Computed values ──────────────────────────────────────

  // Per-file molecule counts are useful as a data-volume total, but summing them
  // overstates the number of compounds when the same structure occurs in more
  // than one file. Project.moleculeCount is the API's identity-deduplicated count.
  const totalFileRows = files.reduce((sum, f) => sum + f.moleculesLibraryCount, 0);
  const uniqueCompoundCount = projects.find(project => project.id === projectId)?.moleculeCount ?? totalFileRows;

  // Sort files for Level 2
  const sortedFiles = useMemo(() => {
    const sorted = [...files];
    switch (level2Sort) {
      case 'recent':
        sorted.sort((a, b) => {
          const aDate = new Date(a.modifiedDate || a.createdDate || 0).getTime();
          const bDate = new Date(b.modifiedDate || b.createdDate || 0).getTime();
          return bDate - aDate;
        });
        break;
      case 'name':
        sorted.sort((a, b) => cleanFileName(a.originalFileName || a.fileName).localeCompare(cleanFileName(b.originalFileName || b.fileName)));
        break;
      case 'molecules':
        sorted.sort((a, b) => b.moleculesLibraryCount - a.moleculesLibraryCount);
        break;
    }
    return sorted;
  }, [files, level2Sort]);

  // ─── Loading & empty states ───────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">Loading compounds...</span>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-24 h-24 mb-6 bg-gradient-to-br from-emerald-100 to-teal-50 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No compound data yet</p>
        <p className="text-gray-400 mt-2 text-center max-w-md">
          Upload SDF, CSV, or Excel files containing molecular structures to see your compounds here.
        </p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LEVEL 2 — FILE OVERVIEW (full-width card grid)
  // ═══════════════════════════════════════════════════════════

  if (navigationLevel === 2) {
    return (
      <div className="flex flex-col animate-fadeIn" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Toolbar */}
        <div className="bg-white dark:bg-gray-900 px-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">{files.length}</span>
              <span className="ml-1">files</span>
              <span className="text-gray-400 font-normal ml-2">
                · {uniqueCompoundCount.toLocaleString()} unique compounds
                {totalFileRows !== uniqueCompoundCount && ` · ${totalFileRows.toLocaleString()} file rows`}
              </span>
            </span>
            {selectedFileIds.length > 0 && (
              <>
                <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
                <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                  {selectedFileIds.length} selected
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setIsMergeOpen(true)}
                    disabled={selectedFileIds.length < 2}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium"
                  >
                    Merge
                  </button>

                  {/* Move to dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsMoveDropdownOpen(!isMoveDropdownOpen)}
                      disabled={isMovingFiles}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium flex items-center gap-1"
                    >
                      {isMovingFiles ? (
                        <>
                          <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                          Moving...
                        </>
                      ) : (
                        <>
                          Move to
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </button>
                    {isMoveDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsMoveDropdownOpen(false)} />
                        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 py-2 min-w-[180px]">
                          {projects.filter(p => p.id !== projectId).length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-400">No other projects</div>
                          ) : (
                            projects.filter(p => p.id !== projectId).map((project) => (
                              <button
                                key={project.id}
                                onClick={() => handleBulkMove(project.id)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
                              >
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                                {project.name}
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Download dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsDownloadDropdownOpen(!isDownloadDropdownOpen)}
                      disabled={isDownloading}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium flex items-center gap-1"
                    >
                      {isDownloading ? (
                        <>
                          <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </button>
                    {isDownloadDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsDownloadDropdownOpen(false)} />
                        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 py-2 min-w-[200px]">
                          <button
                            onClick={() => handleDownload('sdf')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                          >
                            <span>SDF (Structure-Data)</span>
                            <span className="text-xs text-gray-400">.sdf.gz</span>
                          </button>
                          <button
                            onClick={() => handleDownload('csv')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                          >
                            <span>CSV</span>
                            <span className="text-xs text-gray-400">.csv</span>
                          </button>
                          <button
                            onClick={() => handleDownload('smi')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                          >
                            <span>SMILES</span>
                            <span className="text-xs text-gray-400">.smi</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    onClick={handleBulkDelete}
                    className="text-sm text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-medium"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation rows: All Compounds + What's New */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 px-6 py-2">
          <button
            onClick={handleAllCompoundsClick}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200/60 dark:border-blue-800/40 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 transition-all group"
          >
            <svg className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">All Compounds</span>
            <span className="text-xs text-blue-400 dark:text-blue-500">{uniqueCompoundCount.toLocaleString()}</span>
            {(duplicateInfo?.duplicateCount ?? 0) > 0 && (
              <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full">{duplicateInfo!.duplicateCount} dup</span>
            )}
          </button>
          <button
            onClick={handleWhatsNewClick}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200/60 dark:border-emerald-800/40 hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/30 transition-all group"
          >
            <svg className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">What's New</span>
            {totalUnreadCount > 0 && (
              <span className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{totalUnreadCount}</span>
            )}
          </button>
        </div>

        {/* AG Grid File List */}
        <div className="flex-1 min-h-0">
          <FileList
            files={sortedFiles}
            currentProjectId={projectId}
            onFileMoved={refetchFiles}
          />
        </div>

        <MergeDialog
          isOpen={isMergeOpen}
          onClose={() => setIsMergeOpen(false)}
          onComplete={refetchFiles}
        />

        <style>{`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fadeIn { animation: fadeIn 150ms ease-out; }
        `}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LEVEL 1 — WORKING VIEW (file panel + What's New grid)
  // Clicking any file navigates to EditorPage
  // ═══════════════════════════════════════════════════════════

  return (
    <div className="flex flex-1 overflow-hidden" style={{ maxHeight: 'calc(100vh - 220px)' }}>

      {/* ── File panel (reusable component) ── */}
      <FilePanel
        projectId={projectId}
        files={files}
        onWhatsNewClick={handleWhatsNewClick}
        onAllCompoundsClick={handleAllCompoundsClick}
        onExpandToLevel2={handleExpandToLevel2}
        fileUnreadCounts={fileUnreadCounts}
        totalUnreadCount={totalUnreadCount}
        externalNewFileIds={newFileIds}
        totalCompoundCount={uniqueCompoundCount}
        duplicateCount={duplicateInfo?.duplicateCount}
        isAllCompoundsActive={false}
      />

      {/* ── Grid area ── */}
      <div className="flex-1 overflow-hidden flex flex-col min-w-0">
          <>
            {/* What's New header */}
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-800 dark:text-gray-200">What's New</span>
                  {totalUnreadCount > 0 && (
                    <span className="text-sm text-gray-400">{totalUnreadCount.toLocaleString()} unread</span>
                  )}
                </div>

                {/* Read/unread controls */}
                {allMolecules && allMolecules.length > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    {totalUnreadCount > 0 && (
                      <button onClick={markAllRead} className="text-gray-500 hover:text-emerald-600 transition-colors">
                        Mark all read
                      </button>
                    )}
                    {selectedMoleculeIds.length > 0 && (
                      <>
                        <span className="text-gray-300">|</span>
                        <button onClick={markSelectedRead} className="text-gray-500 hover:text-emerald-600 transition-colors">
                          Mark selected read
                        </button>
                        <button onClick={markSelectedUnread} className="text-gray-500 hover:text-emerald-600 transition-colors">
                          Mark selected unread
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Sort dropdown */}
                <select
                  value={whatsNewSort}
                  onChange={e => setWhatsNewSort(e.target.value as WhatsNewSort)}
                  className="ml-auto text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="byFile">By File</option>
                  <option value="unreadFirst">Unread First</option>
                </select>
              </div>
            </div>

            {/* Grid content — What's New */}
            <div className="flex-1 overflow-hidden">
              {isLoadingAll || sortedWhatsNewMolecules === null ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Loading compounds...</span>
                </div>
              ) : sortedWhatsNewMolecules.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-20 h-20 mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">No compounds yet</p>
                  <p className="text-gray-400 mt-1 text-sm">Upload a file to get started.</p>
                </div>
              ) : (
                <>
                  {/* "All caught up" banner when no unread */}
                  {totalUnreadCount === 0 && (
                    <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-emerald-100 dark:border-emerald-900/20 text-center">
                      <span className="text-sm text-emerald-600 dark:text-emerald-400">All caught up! No new compounds since your last visit.</span>
                    </div>
                  )}
                  <MoleculeGrid
                    key={`project-${projectId}-new-${whatsNewSort}`}
                    fileId={`project-${projectId}-new`}
                    clientData={sortedWhatsNewMolecules as any}
                    onRowClick={handleRowClick}
                    onSelectionChange={handleSelectionChange}
                    selectedCompoundId={detailMolecule?.id ?? null}
                    isLocked
                  />
                </>
              )}
            </div>
          </>
      </div>

      {/* ── Compound detail panel ── */}
      <CompoundDetailPanel
        molecule={detailMolecule}
        activeTab={detailTab}
        onClose={() => setDetailMolecule(null)}
        onTabChange={setDetailTab}
        isPinned={isDetailPinned}
        onPinChange={setIsDetailPinned}
        projectId={projectId}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 150ms ease-out; }
      `}</style>
    </div>
  );
};

export default ProjectCompoundsTab;
